import { Grid, Typography } from '@mui/material';
import { entries, flatMap, isEmpty } from 'lodash';
import { Controller, useForm } from 'react-hook-form';
import {
  NumberInputWidget,
  SelectDropdownWidget,
  SwitchButtonWidget,
} from 'src/components/widget';
import {
  OptionalVisualImageSrcEnum,
  SelectHorizontalOptions,
  SelectVerticalOptions,
} from 'src/features/components/editComponentPage/forms/listingWithOptionalVisuals/ListingWithOptionalVisuals.interface';
import { useGetProducts } from 'src/hooks';
import { type IGcfComponentConfig } from 'src/interfaces';
import { lang } from 'src/utils';

import { CustomImageSource } from '../../listingWithOptionalVisuals/customImageSource/CustomImageSource';
import { SelectableImageSource } from '../../listingWithOptionalVisuals/selectableImageSource/SelectableImageSource';
import { EditLOVProductsProps } from '../OOEditProducts.interface';

export const OOLovEditVisualsComponent = (props: EditLOVProductsProps) => {
  const { control, watch } = useForm<IGcfComponentConfig>({
    values: props.selectedComponent.config as any,
  });

  const showDefaultVisuals =
    watch('productList.config.visuals.visualsSource') === 'lineup';
  const showSelectedVisuals =
    watch('productList.config.visuals.visualsSource') === 'selected';
  const showCustomVisuals =
    watch('productList.config.visuals.visualsSource') === 'custom';
  const productList = watch('productList');
  const numberOfVisuals =
    watch('productList.config.visuals.visualsLength') || 3;
  const showVisuals = watch('productList.config.visuals.visible');
  const productCodes = [
    ...new Set(
      flatMap(
        entries(productList?.groups).map(([, value]) =>
          entries(value.products).map(
            ([, productsValue]) => productsValue.productCode,
          ),
        ),
      ),
    ),
  ].sort();

  const { data: products = [] } = useGetProducts(
    !isEmpty(productCodes)
      ? [`product_code.list=${productCodes.join('|')}`]
      : [],
  );

  return (
    <Grid container spacing={2} style={{ padding: '26px' }}>
      <Grid item xs={12}>
        <Controller
          name={`productList.config.visuals.visible`}
          control={control}
          render={({ field: { ref, ...field } }) => (
            <SwitchButtonWidget
              {...field}
              ref={ref}
              onChange={(value) => {
                props.onOverrideConfig(
                  'productList.config.visuals.visible',
                  value,
                );
                field && field.onChange(value);
              }}></SwitchButtonWidget>
          )}
        />
      </Grid>
      {showVisuals && (
        <>
          <Grid item xs={8}>
            <Controller
              name={`productList.config.visuals.visualsLength`}
              control={control}
              defaultValue={3}
              render={({ field: { ref, ...field } }) => (
                <NumberInputWidget
                  label="Number of images"
                  {...field}
                  ref={ref}
                  options={{ max: 3, min: 1 }}
                  onChange={(value) => {
                    props.onOverrideConfig(
                      `productList.config.visuals.visualsLength`,
                      value,
                    );
                    field && field.onChange(value);
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography>{lang.imageAlignment}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Controller
              name={`productList.config.visuals.horizontalAlign`}
              control={control}
              render={({ field: { ref, ...field } }) => (
                <SelectDropdownWidget
                  {...field}
                  ref={ref}
                  label="Horizontal"
                  selectOptions={SelectHorizontalOptions}
                  onChange={(value) => {
                    props.onOverrideConfig(
                      'productList.config.visuals.horizontalAlign',
                      value,
                    );
                    field && field.onChange(value);
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name={`productList.config.visuals.verticalAlign`}
              control={control}
              render={({ field: { ref, ...field } }) => (
                <SelectDropdownWidget
                  {...field}
                  ref={ref}
                  label="Vertical"
                  selectOptions={SelectVerticalOptions}
                  onChange={(value) => {
                    props.onOverrideConfig(
                      'productList.config.visuals.verticalAlign',
                      value,
                    );
                    field && field.onChange(value);
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name={`productList.config.visuals.visualsSource`}
              control={control}
              render={({ field: { ref, ...field } }) => (
                <SelectDropdownWidget
                  {...field}
                  ref={ref}
                  label="Image source"
                  selectOptions={[
                    {
                      label: 'Default',
                      value: OptionalVisualImageSrcEnum.lineup,
                    },
                    {
                      label: 'Selectable',
                      value: OptionalVisualImageSrcEnum.selected,
                    },
                    {
                      label: 'Custom',
                      value: OptionalVisualImageSrcEnum.custom,
                    },
                  ]}
                  onChange={(value) => {
                    props.onOverrideConfig(
                      'productList.config.visuals.visualsSource',
                      value,
                    );
                    field && field.onChange(value);
                  }}
                />
              )}
            />
          </Grid>

          {(showSelectedVisuals || showDefaultVisuals) && (
            <Grid item xs={12}>
              <Controller
                name="productList.config.visuals.items"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <SelectableImageSource
                    {...field}
                    ref={ref}
                    productList={props?.selectedComponent?.config?.productList}
                    products={products}
                    onChange={(value, setChanged) => {
                      props.onOverrideConfig('productList', value);
                      field && field.onChange(value);
                      if (
                        setChanged &&
                        !props.isDefaultImageSourceUpdated &&
                        props.onDefaultImageSourceUpdated
                      ) {
                        props.onDefaultImageSourceUpdated(setChanged);
                      }
                    }}
                    isDefaultProductUpdated={props.isDefaultImageSourceUpdated}
                    isDefaultImageSource={showDefaultVisuals}
                    visualsLength={numberOfVisuals}
                  />
                )}
              />
            </Grid>
          )}
          {showCustomVisuals && (
            <Grid item xs={12}>
              <Controller
                name="images"
                control={control}
                render={({ field: { ref, ...field } }) => (
                  <CustomImageSource
                    {...field}
                    ref={ref}
                    onChange={(value) => {
                      props.onOverrideConfig('images', value);
                    }}
                  />
                )}
              />
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
};
